import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gift, Users, ShoppingBag, Sparkles, Wallet, ChevronRight, Shirt, Smartphone, Plane, UtensilsCrossed, Heart, Home, Tv, BadgePercent } from "lucide-react";
import { ProductCard } from "@/components/site/ProductCard";

const HERO_IMG = "https://images.unsplash.com/photo-1602526432604-029a709e131c?crop=entropy&cs=srgb&fm=jpg&q=80&w=900";
const ICONS = { Shirt, Smartphone, Plane, UtensilsCrossed, Heart, Sparkles, Home, Tv };
const CAT_COLORS = ["bg-secondary-200", "bg-pink-200", "bg-primary-200", "bg-cyan-200", "bg-lime-200", "bg-amber-200", "bg-fuchsia-200", "bg-sky-200"];

export const SectionHead = ({ title, sub, to, testId }) => (
  <div className="flex items-end justify-between gap-4 mb-5 sm:mb-8">
    <div>
      <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-primary-900 tracking-tight">{title}</h2>
      {sub && <p className="text-sm sm:text-base text-primary-500 mt-1">{sub}</p>}
    </div>
    {to && (
      <Link to={to} className="shrink-0 inline-flex items-center gap-1 text-sm font-bold text-primary-600 hover:text-primary-800" data-testid={testId}>
        See all<ChevronRight className="w-4 h-4" />
      </Link>
    )}
  </div>
);

export const HeroSection = ({ user, slider }) => (
  <section className="mv-hero-bg relative overflow-hidden" data-testid="home-hero">
    <div className="absolute inset-0 mv-dots opacity-60" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14 sm:py-20 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
      <div className="text-white">
        <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold">
          <span className="w-2 h-2 rounded-full bg-secondary-400 animate-pulse" />India's playful cashback club
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mt-5">
          Shop anything.<br />
          <span className="relative inline-block text-secondary-400">Get paid back<svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none"><path d="M2 9C50 3 150 3 198 9" stroke="#FBBF24" strokeWidth="4" strokeLinecap="round" /></svg></span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-primary-100 max-w-lg">Up to 25% cashback from 500+ brands, best-price comparison across stores, and rewards every time a friend shops.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={user ? "/products" : "/register"} className="inline-flex items-center h-12 sm:h-14 px-6 sm:px-8 rounded-full bg-secondary-500 hover:bg-secondary-400 text-primary-900 font-extrabold shadow-[0_5px_0_#B45309] active:translate-y-1 active:shadow-none transition-transform" data-testid="hero-cta-btn">
            {user ? "Grab today's deals" : "Start earning free"}<ArrowRight className="w-5 h-5 ml-2" />
          </Link>
          <Link to="/brands" className="inline-flex items-center h-12 sm:h-14 px-6 sm:px-8 rounded-full border-2 border-white/40 text-white font-bold hover:bg-white/10 transition-colors" data-testid="browse-deals-btn">Explore brands</Link>
        </div>
        <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
          {[["50K+", "members"], ["₹1Cr+", "paid back"], ["500+", "brands"]].map(([v, l]) => (
            <div key={l} className="rounded-2xl bg-white/10 backdrop-blur px-3 py-3 text-center">
              <p className="font-heading text-xl sm:text-2xl font-extrabold text-secondary-400">{v}</p>
              <p className="text-[11px] sm:text-xs text-primary-100">{l}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="relative hidden sm:block">
        <div className="relative rounded-[2.5rem] overflow-hidden border-[6px] border-white/20 rotate-2 shadow-2xl aspect-[4/3]">
          <img src={slider?.image_url || HERO_IMG} alt={slider?.title || "Cashback rewards"} className="w-full h-full object-cover" />
        </div>
        <div className="mv-float absolute -left-6 bottom-8 bg-white rounded-2xl p-4 shadow-xl flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-secondary-500 flex items-center justify-center"><Wallet className="w-5 h-5 text-primary-900" /></span>
          <div><p className="text-xs text-primary-400">Cashback credited</p><p className="font-extrabold text-primary-900">+ ₹1,240</p></div>
        </div>
        <div className="mv-float absolute -right-4 top-6 bg-pink-500 text-white rounded-2xl px-4 py-3 shadow-xl font-extrabold [animation-delay:1.5s]">25% OFF</div>
      </div>
    </div>
  </section>
);

export const CategoryStrip = ({ categories }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
    <div className="bg-white rounded-3xl shadow-xl shadow-primary-900/10 p-4 sm:p-6 flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar" data-testid="home-categories">
      {categories.map((cat, i) => {
        const Icon = ICONS[cat.icon] || ShoppingBag;
        return (
          <Link key={cat.id} to={`/products/${cat.name.toLowerCase()}`} className="group shrink-0 w-20 sm:w-24 flex flex-col items-center gap-2" data-testid={`category-${cat.name.toLowerCase()}`}>
            <span className={`w-16 h-16 sm:w-20 sm:h-20 rounded-[1.4rem] ${CAT_COLORS[i % CAT_COLORS.length]} flex items-center justify-center group-hover:-rotate-6 group-hover:scale-105 transition-transform`}>
              <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-primary-800" />
            </span>
            <span className="text-xs sm:text-sm font-bold text-primary-800 text-center leading-tight">{cat.name}</span>
          </Link>
        );
      })}
    </div>
  </section>
);

const initials = (n) => n.split(" ").map((w) => w[0]).join("").substring(0, 2).toUpperCase();

export const BrandRail = ({ brands }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
    <SectionHead title="Partner brands" sub="Tap a brand, shop as usual, cashback lands in your wallet" to="/brands" testId="home-brands-see-all" />
    <div className="flex sm:grid sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 overflow-x-auto no-scrollbar pb-2">
      {brands.slice(0, 12).map((b) => (
        <Link key={b.id} to={`/brands/${b.id}`} className="group shrink-0 w-32 sm:w-auto bg-white rounded-3xl border-2 border-primary-100 p-4 flex flex-col items-center gap-3 hover:border-secondary-400 hover:-translate-y-1 transition-[transform,border-color]" data-testid={`home-brand-card-${b.id}`}>
          <span className="w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center overflow-hidden">
            {b.logo_url ? <img src={b.logo_url} alt={b.name} className="max-w-[80%] max-h-[80%] object-contain" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("span"), { textContent: initials(b.name), className: "font-extrabold text-primary-600" })); }} /> : <span className="font-extrabold text-primary-600">{initials(b.name)}</span>}
          </span>
          <span className="text-sm font-bold text-primary-900 truncate max-w-full">{b.name}</span>
          <span className="text-[11px] font-extrabold bg-secondary-100 text-secondary-800 rounded-full px-2.5 py-0.5">{b.cashback_percent}% back</span>
        </Link>
      ))}
    </div>
  </section>
);

export const DealsGrid = ({ products }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
    <SectionHead title="Top cashback deals" sub="Handpicked offers — compare prices before you buy" to="/products" testId="home-deals-see-all" />
    {products.length ? (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        {products.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    ) : (
      <div className="text-center py-12 bg-primary-50 rounded-3xl text-primary-500">No deals yet. Check back soon!</div>
    )}
  </section>
);

const STEPS = [
  { icon: ShoppingBag, title: "Shop via M Ventures", text: "Pick a deal or brand and shop as usual on the store.", color: "bg-secondary-500 text-primary-900" },
  { icon: BadgePercent, title: "Cashback lands", text: "Your cashback is tracked and credited to your wallet.", color: "bg-pink-500 text-white" },
  { icon: Users, title: "Refer & repeat", text: "Invite friends and earn on their purchases, level after level.", color: "bg-primary-600 text-white" },
];

export const HowItWorks = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
    <SectionHead title="How it works" sub="Three steps. Zero fuss." />
    <div className="grid md:grid-cols-3 gap-4">
      {STEPS.map((s, i) => (
        <div key={s.title} className="relative rounded-3xl bg-white border-2 border-primary-100 p-6">
          <span className="absolute top-4 right-5 font-heading text-5xl font-extrabold text-primary-100">0{i + 1}</span>
          <span className={`w-14 h-14 rounded-2xl flex items-center justify-center ${s.color}`}><s.icon className="w-7 h-7" /></span>
          <h3 className="font-heading text-lg font-bold text-primary-900 mt-4">{s.title}</h3>
          <p className="text-sm text-primary-500 mt-1">{s.text}</p>
        </div>
      ))}
    </div>
  </section>
);

export const JoinBanner = ({ user }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
    <div className="relative overflow-hidden rounded-[2rem] bg-secondary-500 p-8 sm:p-12 grid md:grid-cols-[1fr_auto] gap-6 items-center">
      <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-pink-400/40" />
      <div className="absolute right-24 -bottom-16 w-40 h-40 rounded-full bg-primary-500/30" />
      <div className="relative">
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-primary-900 tracking-tight">{user ? "Invite friends, earn forever" : "Ready to get paid for shopping?"}</h2>
        <p className="text-primary-800 mt-2 max-w-xl">{user ? "Share your referral link and earn multi-level commissions on every purchase." : "Join free in under a minute. Sellers can open their own store too."}</p>
      </div>
      <div className="relative flex flex-wrap gap-3">
        <Link to={user ? "/referrals" : "/register"} className="inline-flex items-center h-12 px-6 rounded-full bg-primary-700 hover:bg-primary-800 text-white font-bold" data-testid="cta-register-btn">
          <Gift className="w-5 h-5 mr-2" />{user ? "Refer & earn" : "Create free account"}
        </Link>
        {!user && <Link to="/seller/register" className="inline-flex items-center h-12 px-6 rounded-full bg-white text-primary-800 font-bold" data-testid="cta-become-seller-btn">Become a seller</Link>}
      </div>
    </div>
  </section>
);
